import React, { useEffect } from "react";
import Card from "components/card";
import Pagination from "@material-ui/lab/Pagination";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import useCampaigns from "../useCampaign";
import { FiDownload } from "react-icons/fi";

function getCampaignStatus(campaign) {
  const now = new Date();
  const start = campaign.startDate ? new Date(campaign.startDate) : null;
  const end = campaign.endDate ? new Date(campaign.endDate) : null;
  if (!campaign.isActive) return "Inactive";
  if (start && end) {
    if (end < now) return "Expired";
    if (start > now) return "Upcoming";
    return "Active";
  }
  return "—";
}

function CampaignTable(props) {
  const {
    tableData,
    handleItemsPerPageChange,
    handlePageChange,
    currentPage,
    totalCount,
    itemsPerPage,
    onCampaignDeactivated,
  } = props;

  // Add columnHelper here
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          CAMPAIGN NAME
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("couponCode", {
      id: "couponCode",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          COUPON CODE
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() ?? "-"}
        </p>
      ),
    }),
    columnHelper.accessor("startDate", {
      id: "startDate",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          START DATE
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {new Date(info?.getValue())?.toLocaleString()}
        </p>
      ),
    }),
    columnHelper.accessor("endDate", {
      id: "endDate",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">END DATE</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {new Date(info?.getValue())?.toLocaleString()}
        </p>
      ),
    }),
    columnHelper.accessor(
      (row) => getCampaignStatus(row),
      {
        id: "status",
        header: () => (
          <p className="text-sm font-bold text-gray-600 dark:text-white">
            STATUS
          </p>
        ),
        cell: (info) => (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {getCampaignStatus(info.row.original)}
          </p>
        ),
      }
    ),
    columnHelper.accessor("actions", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          ACTIONS
        </p>
      ),
      cell: (data) => {
        const campaign = data.row.original;
        const isActive = campaign.isActive;
        return (
          <div className="flex items-center gap-3 justify-start">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-blue-600 flex items-center gap-1"
              title="Download redemptions report (CSV)"
              disabled={!campaign._id}
              onClick={async () => {
                await downloadCampaignReportCsv(
                  campaign._id,
                  (campaign.name || "campaign").replace(/[^a-z0-9-_]/gi, "-")
                );
              }}
            >
              <FiDownload className="inline" /> Download report
            </button>
            <button
              type="button"
              disabled={!isActive}
              className="text-sm text-amber-600 hover:text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-gray-400"
              title={
                isActive
                  ? "Deactivate campaign"
                  : "Campaign is inactive"
              }
              onClick={async () => {
                if (
                  isActive &&
                  window.confirm(
                    `Deactivate campaign "${campaign.name}"? This will set the campaign as inactive.`
                  )
                ) {
                  const res = await deleteCampaign(campaign._id);
                  if (res && typeof onCampaignDeactivated === "function")
                    onCampaignDeactivated();
                }
              }}
            >
              Deactivate
            </button>
          </div>
        );
      },
    }),
  ];

  const [sorting, setSorting] = React.useState([]);
  const [data, setData] = React.useState(() => [...tableData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  useEffect(() => {
    setData(tableData);
  }, [tableData]);

  const { deleteCampaign, downloadCampaignReportCsv } = useCampaigns();


  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6"}>
      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start"
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() ? (
                          <span>
                            {header.column.getIsSorted() === "asc" ? "↑" : "↓"}
                          </span>
                        ) : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table
              .getRowModel()
              .rows.slice(0)
              .map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          className="min-w-[150px] border-white/0 py-3 pr-4"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>

        <div className="mt-4 flex items-center justify-between">
          <Pagination
            count={Math.max(1, Math.ceil(totalCount / itemsPerPage))}
            page={currentPage}
            onChange={handlePageChange}
            size="small"
            showFirstButton
            showLastButton
          />
          <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    </Card>
  );
}

export default CampaignTable;
